import React, { useState, useEffect, useMemo, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import rehypeRaw from "rehype-raw";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import ImageComponent from "./ImageComponent";
import ToolTipAndLink from "./ToolTipAndLink";
import "katex/dist/katex.min.css";
import "../styles/Content.scss";
import CoinShower from "./common/Coinshower";
import { checkIsAdmin } from "../helper/authUtils";

const Content = ({
  content,
  handleCompletedClick,
  isMarkAsComplete,
  setIsMarkAsComplete,
}) => {
  const nonObjectContents = useRef([]);
  const [imageVideoJson, setimageVideoJson] = useState([]);
  const [showCoinShower, setShowCoinShower] = useState(false);

  useEffect(() => {
    const isAdmin = checkIsAdmin();
    if (isMarkAsComplete && !isAdmin) {
      setShowCoinShower(true);
    }
  }, [isMarkAsComplete]);

  const cleanMarkdown = (text = "") => {
    return text?.replace(/^```(?:markdown)?\s*|\s*```$/g, "");
  };

  const splitContentIntoJsonObjects = (content) => {
    const regexJson = /```json([\s\S]*?)```/g;
    const regexGeneric = /```([\s\S]*?)```/g;
    const regexJsonSafe = /```json([\s\S]*?)/g;
    const regexGenericSafe = /```([\s\S]*?)/g;
    const regexNormalArray = /^\s*\[\s*{[\s\S]*}\s*]\s*$/ms;
    const regexNormalObject = /^\s*\{.*\}\s*$/s;

    let regex = regexJson.test(content)
      ? regexJson
      : regexGeneric.test(content)
        ? regexGeneric
        : regexJsonSafe.test(content)
          ? regexJsonSafe
          : regexGenericSafe.test(content)
            ? regexGenericSafe
            : regexNormalArray.test(content)
              ? regexNormalArray
              : regexNormalObject.test(content)
                ? regexNormalObject
                : null;

    let result = [];
    let lastIndex = 0;
    let match;

    if (regex) {
      regex.lastIndex = 0;

      while ((match = regex.exec(content)) !== null) {
        const matchStart = match.index;
        const matchEnd = matchStart + match[0].length;

        if (matchStart > lastIndex) {
          result.push({
            content: content.slice(lastIndex, matchStart),
          });
        }

        result.push({
          content: match[0],
        });

        lastIndex = matchEnd;

        if (!regex.global) break;
      }
    }

    if (lastIndex < content.length) {
      result.push({
        content: content.slice(lastIndex).trim() + `\n`,
      });
    }

    return result;
  };

  const MarkdownRenderer = ({ markdown, index, type }) => {
    const splitData = useMemo(() => {
      if (type === "content" || type === "active_recall_content") {
        return [{ content: markdown }];
      }
      return splitContentIntoJsonObjects(markdown);
    }, [markdown, type]);

    useEffect(() => {
      if (splitData?.jsonArr) {
        setimageVideoJson((prev) => [...prev, splitData.jsonArr]);
      }
    }, [splitData?.jsonArr]);

    return splitData?.map((value, key) => {
      const val = Object.values(value)[0];

      if (typeof val !== "string" || val === "") return null;

      const jsonFence = val.match(/```json\s*([\s\S]*?)\s*```/);
      const objectOrArray =
        val.match(/^\s*\[[\s\S]*\]\s*$/ms) || val.match(/^\s*\{[\s\S]*\}\s*$/s);

      let isArrayString = false;
      let parsed = null;
      let typeClass = null;

      if (jsonFence) {
        const jsonStr = (jsonFence[1] || "").trim();
        try {
          isArrayString = /^\s*\[[\s\S]*\]\s*$/.test(jsonStr);
          parsed = JSON.parse(jsonStr);
        } catch (error) {
          parsed = null;
        }
      } else if (objectOrArray) {
        const jsonStr = (objectOrArray[0] || "").trim();
        try {
          isArrayString = /^\s*\[[\s\S]*\]\s*$/.test(jsonStr);
          parsed = JSON.parse(jsonStr);
        } catch (error) {
          parsed = null;
        }
      }

      if (
        (!parsed || typeof parsed !== "object") &&
        (val.startsWith("#") || val.startsWith("*")) &&
        !val.startsWith("`")
      ) {
        nonObjectContents.current.push(val);
      }

      if (value && Object.values(value)[0]?.length > 0) {
        const jsonMatch = val.match(/```json\n([\s\S]*?)\n```/);
        if (jsonMatch) {
          let jsonString = jsonMatch[1];
          jsonString = jsonString.trim();

          try {
            parsed = JSON.parse(jsonString);
            if (
              (parsed?.type && parsed.type === "amazing_fact") ||
              parsed.type === "highlighted_content"
            ) {
              typeClass = "amazing_fact";
            } else if (parsed?.type && parsed.type === "relatedQuestions") {
              typeClass = "relatedQuestions";
            } else if (parsed?.type !== "video" && parsed?.type !== "image") {
              typeClass = "ctc";
            }
          } catch (e) {
            console.error("Invalid JSON:", e);
          }
        }
      }

      const components = {
        image: ImageComponent,
      };

      return (
        value &&
        Object.values(value)[0]?.length > 0 && (
          <div
            className={`mark-down-container d-flex-col gap-4 ${
              isArrayString ? "ctc-main-container" : null
            } type-${!isArrayString ? typeClass : ""} ${
              typeClass === "relatedQuestions" ? "hide-first-child" : ""
            }`}
            key={`${index}-${key}`}
          >
            <ReactMarkdown
              remarkPlugins={[remarkMath, remarkGfm]}
              rehypePlugins={[rehypeRaw, rehypeKatex]}
              components={{
                code({ node, inline, className, children, ...props }) {
                  const isTrueInline =
                    inline ||
                    (!className &&
                      String(children).trim().length < 50 &&
                      !String(children).includes("\n"));

                  if (isTrueInline) {
                    return (
                      <code
                        className="inline-code"
                        style={{
                          backgroundColor: "#f0f0f0",
                          borderRadius: "3px",
                          padding: "0.2em 0.4em",
                          fontFamily: "monospace",
                          fontSize: "0.9em",
                          color: "#d63384",
                          border: "1px solid #ddd",
                          display: "inline",
                        }}
                        {...props}
                      >
                        {children}
                      </code>
                    );
                  }

                  if (!inline) {
                    const text = String(children).trim();
                    const looksJson =
                      (className && className.includes("language-json")) ||
                      text.startsWith("{") ||
                      text.startsWith("[");
                    if (looksJson) {
                      try {
                        const json = JSON.parse(text);
                        if (json && Array.isArray(json)) {
                          return (
                            <div className="content-array-container">
                              {json.map((item, index) => {
                                const Component = components[item?.type];
                                return Component ? (
                                  <Component
                                    key={index}
                                    content={item}
                                    imageVideoJson={imageVideoJson}
                                    setimageVideoJson={setimageVideoJson}
                                  />
                                ) : null;
                              })}
                            </div>
                          );
                        }
                        const Component = components[json?.type];
                        return Component ? (
                          <Component
                            content={json}
                            imageVideoJson={imageVideoJson}
                            setimageVideoJson={setimageVideoJson}
                          />
                        ) : (
                          <pre className="content-markdown">{children}</pre>
                        );
                      } catch {}
                    }
                    return (
                      <SyntaxHighlighter
                        language={
                          className && className.startsWith("language-")
                            ? className.replace("language-", "")
                            : "javascript"
                        }
                        style={vscDarkPlus}
                        customStyle={{
                          maxWidth: "100%",
                          overflowWrap: "break-word",
                          wordBreak: "word-break",
                          whiteSpace: "pre-wrap",
                        }}
                        {...props}
                      >
                        {String(children).replace(/\n$/, "")}
                      </SyntaxHighlighter>
                    );
                  }
                  return (
                    <code
                      className={`content-markdown ${className}`}
                      {...props}
                      style={{
                        display: "inline-block",
                        maxWidth: "100%",
                        overflowWrap: "break-word",
                        wordBreak: "break-word",
                        whiteSpace: "pre-wrap !important",
                      }}
                    >
                      <div
                        style={{
                          display: "block",
                          maxWidth: "100%",
                        }}
                      >
                        {children}
                      </div>
                    </code>
                  );
                },
                table({ node, children }) {
                  return <table className="markdown-table">{children}</table>;
                },
                th({ node, children }) {
                  return (
                    <th className="tooltip-header">
                      <ToolTipAndLink value={children} />
                    </th>
                  );
                },
                a({ node, children, href, title, ...props }) {
                  if (href === "#" && title) {
                    return (
                      <span style={{ display: "inline-block" }}>
                        <ToolTipAndLink value={title}>
                          <span
                            {...props}
                            style={{
                              borderBottom:
                                "1px dotted var(--black-background)",
                              cursor: "help",
                            }}
                          >
                            {children}
                          </span>
                        </ToolTipAndLink>
                      </span>
                    );
                  }
                  return (
                    <a
                      href={href}
                      title={title}
                      target="_blank"
                      rel="noopener noreferrer"
                      {...props}
                    >
                      {children}
                    </a>
                  );
                },
              }}
            >
              {Object.values(value)[0]}
            </ReactMarkdown>
          </div>
        )
      );
    });
  };

  return (
    <div className="content-container custom-scrollbar">
      {showCoinShower && (
        <CoinShower
          show={showCoinShower}
          onHide={() => setIsMarkAsComplete(false)}
        />
      )}

      <div className="content-main">
        {content?.length > 0 ? (
          content.map((item, index) => (
            <div key={item?.id ?? `content-${index}`} className="content-card">
              <div className="content-body custom-scrollbar">
                <MarkdownRenderer
                  markdown={cleanMarkdown(item.content)}
                  index={index}
                  type={item.type}
                />
              </div>
            </div>
          ))
        ) : (
          <div className="mcq-empty">No Content provided.</div>
        )}
      </div>
    </div>
  );
};

export default Content;
